import { FC, ReactNode } from 'react';
import {
	AutoComplete,
	AutoCompleteProps,
	Button,
	ButtonProps,
	Checkbox,
	CheckboxProps,
	Col,
	DatePicker,
	DatePickerProps,
	Divider,
	DividerProps,
	Form,
	Input,
	InputNumber,
	InputNumberProps,
	InputProps,
	Radio,
	RadioGroupProps,
	Row,
	Select,
	SelectProps,
	Switch,
	SwitchProps,
	TimePicker,
	TimePickerProps,
	Upload,
	UploadProps,
} from 'antd';
import { FormItemProps, FormProps, Rule } from 'antd/es/form';
import { InboxOutlined, SearchOutlined } from '@ant-design/icons';
import { PasswordProps, TextAreaProps } from 'antd/es/input';
import { CheckboxGroupProps } from 'antd/es/checkbox';
import { RangePickerProps } from 'antd/es/date-picker';
import CollapsibleList from './CollapsibleList';
import { RangePickerTimeProps } from 'antd/es/time-picker';
import { Dayjs } from 'dayjs';

const FIELD_WIDTH_MAPPING = {
	'1/1': 24,
	'1/2': 12,
	'1/3': 8,
	'1/4': 6,
} as const;

const FIELD_COMPONENT_MAPPING = {
	'text': (props?: InputProps) => <Input {...props} />,
	'textarea': (props?: TextAreaProps) => (
		<Input.TextArea
			rows={3}
			{...props}
		/>
	),
	'password': (props?: PasswordProps) => <Input.Password {...props} />,
	'number': (props?: InputNumberProps) => (
		<InputNumber
			{...props}
			style={{
				width: '100%',
				...props?.style,
			}}
		/>
	),
	'select': (props?: SelectProps) => <Select {...props} />,
	'auto-complete': (props?: AutoCompleteProps) => (
		<AutoComplete
			suffixIcon={<SearchOutlined />}
			allowClear
			{...props}
		/>
	),
	'date': (props?: DatePickerProps) => (
		<DatePicker
			{...props}
			style={{
				width: '100%',
				...props?.style,
			}}
		/>
	),
	'date-range': (props?: RangePickerProps) => (
		<DatePicker.RangePicker
			{...props}
			style={{
				width: '100%',
				...props?.style,
			}}
		/>
	),
	'time': (props?: TimePickerProps) => (
		<TimePicker
			{...props}
			style={{
				width: '100%',
				...props?.style,
			}}
		/>
	),
	'time-range': (props?: RangePickerTimeProps<Dayjs>) => (
		<TimePicker.RangePicker
			{...props}
			style={{
				width: '100%',
				...props?.style,
			}}
		/>
	),
	'upload': (props?: UploadProps) => (
		<Upload.Dragger
			name="files"
			listType="picture"
			{...props}
		>
			<p className="ant-upload-drag-icon">
				<InboxOutlined />
			</p>
			<p className="ant-upload-text">
				Click or drag file to this area to upload
			</p>
			<p className="ant-upload-hint">Support for a single or bulk upload.</p>
		</Upload.Dragger>
	),
	'switch': (props?: SwitchProps) => <Switch {...props} />,
	'checkbox': (props?: CheckboxProps) => <Checkbox {...props} />,
	'checkbox-group': (props?: CheckboxGroupProps) => (
		<Checkbox.Group {...props} />
	),
	'radio-group': (props?: RadioGroupProps) => <Radio.Group {...props} />,
} as const;

type FieldKey = string | number;

type FieldName = FieldKey | FieldKey[];

type Field = {
	name: FieldName;
	label?: string;
	width?: keyof typeof FIELD_WIDTH_MAPPING;
};

type FieldRecord = Field & {
	type: 'record';
	config?: {
		fields: FieldType[];
	};
};

type FieldComponent<T extends keyof typeof FIELD_COMPONENT_MAPPING> = Field & {
	type: T;
	rules?: Rule[];
	config?: Parameters<(typeof FIELD_COMPONENT_MAPPING)[T]>[0];
};

type FieldDivider = {
	type: 'divider';
	config?: DividerProps;
};

type FieldButton = {
	type: 'button';
	config?: ButtonProps;
	width?: keyof typeof FIELD_WIDTH_MAPPING;
};

export type FieldType =
	| FieldComponent<'text'>
	| FieldComponent<'textarea'>
	| FieldComponent<'password'>
	| FieldComponent<'number'>
	| FieldComponent<'select'>
	| FieldComponent<'auto-complete'>
	| FieldComponent<'date'>
	| FieldComponent<'date-range'>
	| FieldComponent<'time'>
	| FieldComponent<'time-range'>
	| FieldComponent<'upload'>
	| FieldComponent<'switch'>
	| FieldComponent<'checkbox'>
	| FieldComponent<'checkbox-group'>
	| FieldComponent<'radio-group'>
	| FieldButton
	| FieldRecord
	| FieldDivider;

export const renderField = (
	field: FieldType & { key: number | string },
	values?: Record<string, unknown>,
) => {
	const getComponent = FIELD_COMPONENT_MAPPING[
		field.type as keyof typeof FIELD_COMPONENT_MAPPING
	] as (
		config: FieldComponent<keyof typeof FIELD_COMPONENT_MAPPING>['config'],
	) => JSX.Element;

	const getFormItemProps = (): FormItemProps | null => {
		switch (field.type) {
			case 'checkbox':
				return {
					valuePropName: 'checked',
				};
			case 'upload':
				return {
					valuePropName: 'fileList',
					getValueFromEvent: (e) => (Array.isArray(e) ? e : e?.fileList),
				};
			default:
				return null;
		}
	};

	const render = () => {
		switch (field.type) {
			case 'record':
				return (
					<Form.Item
						label={field.label}
						style={{
							marginBottom: 0,
						}}
					>
						<Form.List name={field.name}>
							{(listFields, { add, remove, move }) => (
								<CollapsibleList
									items={listFields.map((listField, listFieldIndex) => {
										const renderTitle = () => {
											if (!values || !((field.name as FieldKey) in values))
												return;
											const fieldValues = values[
												field.name as FieldKey
											] as (typeof values)[];
											const fieldAlias = field?.config?.fields?.find(
												(field) => 'name' in field,
											);

											if (
												!fieldValues ||
												!fieldAlias ||
												!('name' in fieldAlias)
											)
												return;
											return fieldValues[listFieldIndex]?.[
												fieldAlias.name as FieldKey
											] as string;
										};

										return {
											label:
												renderTitle() ||
												`${field.label ?? 'Entry'} ${listFieldIndex + 1}`,
											children: (
												<Row gutter={[16, 16]}>
													{field.config?.fields.map(
														(fieldChild, childIndex) => {
															if (
																fieldChild.type === 'divider' ||
																fieldChild.type === 'button'
															) {
																return renderField(
																	{ ...fieldChild, key: childIndex },
																	values,
																);
															}

															const name = [
																listFieldIndex,
																...(Array.isArray(fieldChild.name)
																	? fieldChild.name
																	: [fieldChild.name]),
															] as FieldName;

															return renderField(
																{ ...fieldChild, name, key: childIndex },
																values,
															);
														},
													)}
												</Row>
											),
											onRemove: () => remove(listField.name),
											onMoveUp: () => move(listFieldIndex, listFieldIndex - 1),
											onMoveDown: () =>
												move(listFieldIndex, listFieldIndex + 1),
										};
									})}
									onAddNew={() => add()}
									addButtonProps={{
										children: `Add ${field.label}`,
										block: true,
									}}
								/>
							)}
						</Form.List>
					</Form.Item>
				);
			case 'divider':
				return <Divider {...field.config} />;
			case 'button':
				return (
					<Button
						block
						{...field.config}
					/>
				);
			default:
				return (
					<Form.Item
						name={field.name}
						label={field.label}
						rules={field.rules}
						style={{
							marginBottom: 0,
						}}
						{...getFormItemProps()}
					>
						{getComponent(field.config)}
					</Form.Item>
				);
		}
	};

	return (
		<Col
			key={field.key}
			span={
				field.type === 'divider'
					? 24
					: field.type === 'button'
						? FIELD_WIDTH_MAPPING[field.width || '1/1'] ?? 24
						: FIELD_WIDTH_MAPPING[field.width || '1/1']
			}
		>
			{render()}
		</Col>
	);
};

type FormBuilderProps = {
	fields: FieldType[];
	form?: ReturnType<typeof useFormBuilder>['form'];
	values?: ReturnType<typeof useFormBuilder>['values'];
	config?: Omit<FormProps, 'form'>;
	children?: ReactNode;
	onFinish?: (values: ReturnType<typeof useFormBuilder>['values']) => void;
};

const FormBuilder: FC<FormBuilderProps> = ({
	fields,
	values,
	form,
	config,
	children,
	onFinish,
}) => {
	return (
		<Form
			{...config}
			form={form}
			layout="vertical"
			scrollToFirstError={true}
			onFinish={(values) => onFinish?.(values)}
		>
			<Row gutter={[16, 16]}>
				{fields.map((field, key) => {
					return renderField({ ...field, key }, values);
				})}
				{children}
			</Row>
		</Form>
	);
};

export default FormBuilder;

export function useFormBuilder<T = unknown>() {
	const [form] = Form.useForm<T>();
	const values = Form.useWatch(null, form);
	return { form, values };
}
